import { useEffect, useState, type FunctionComponent } from 'react';
import find from 'lodash/find';
import get from 'lodash/get';
import {
  addAddress as addAddressService,
  clearUserCache,
  COUNTRY_SELECTION,
  getCountryId,
  removeAddress as removeAddressService,
  updateAddress,
  verifyAddress as verifyAddressService,
} from '@fff-web/fff-utilities';
import AddAddressButton from '../AddAddressButton';
import AddressForm from '../AddressForm';
import AddressList from '../AddressList';
import AddressVerificationModal from '../AddressVerificationModal';
import { Times } from '../Icon';
import Modal from '../Modal';
import { showErrorToast, Toast } from '../Toast';
import * as styles from './Address.module.scss';

type NormalizedAddress = {
  userAddress: {
    id: number;
    firstName: string;
    lastName: string;
    contactNumber: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    // TODO: Verify which one is correct because both are used (zipcode vs zipCode vs zip)
    zipcode: string;
    zipCode: string;
    zip: string;

    country: string;
    isDefault: boolean;

    address1?: string;
    address2?: string;
    phone?: string;
    region?: string;
    primaryLine: string;
    countryCode: string;
  };
};

type AddressProps = {
  addressesData: NormalizedAddress[];
  selectedAddressId?: number;
  setSelectedAddressId?: (id: number) => void;
  showRadioSelectors?: boolean;
  layoutType: string;
  onNewAddressCreated: (id: number) => void;
};

export const ADDRESS_LAYOUT_TYPES = {
  checkout: 'CHECKOUT',
  userAccount: 'USER_ACCOUNT',
};

const ADDRESS_MODAL_NAMES = {
  edit: 'EDIT_ADDRESS_MODAL',
  add: 'ADD_ADDRESS_MODAL',
};

const ADDRESS_MODAL_TITLE = {
  [ADDRESS_MODAL_NAMES.edit]: 'Edit Address',
  [ADDRESS_MODAL_NAMES.add]: 'Add New Address',
};

const handleAddressValidation = (deliveryStatus: string, fffUndeliverable: boolean) => {
  if (deliveryStatus === 'deliverable') {
    if (fffUndeliverable) return 'suggested';
    return 'success';
  }
  return 'undeliverable';
};

const Address: FunctionComponent<AddressProps> = ({
  addressesData,
  selectedAddressId,
  setSelectedAddressId,
  layoutType,
  onNewAddressCreated,
}) => {
  const [addresses, setAddresses] = useState<NormalizedAddress[]>([]);
  const [defaultAddressId, setDefaultAddressId] = useState<number | null>(null);
  const [currentAddressId, setCurrentAddressId] = useState<number | null>(null);
  const [currentModal, setCurrentModal] = useState<string>('');
  const [isOpenAddressModal, setIsOpenAddressModal] = useState<boolean>(false);
  const [verifyingAddress, setVerifyingAddress] = useState<boolean>(false);
  const [verificationModalOpen, setVerificationModalOpen] = useState<unknown>('');
  const [verificationModalData, setVerificationModalData] = useState<{
    submitted: unknown;
    verified: unknown;
  }>({
    submitted: {},
    verified: {},
  });
  const [payload, setPayload] = useState<Record<string, unknown> | null>(null);
  const [stateList, setStateList] = useState<unknown[]>([]);

  const currentAddress =
    addresses.find((address) => address.userAddress.id === currentAddressId)?.userAddress ||
    ({} as NormalizedAddress['userAddress']);

  const {
    firstName,
    lastName,
    contactNumber,
    addressLine1,
    addressLine2,
    city,
    state,
    zipcode,
    country,
    isDefault,
  } = currentAddress;
  const defaultAddressIndex = addresses?.findIndex((item) => item.userAddress.isDefault) ?? -1;
  const defaultCountry = addresses?.[defaultAddressIndex]?.userAddress?.country || 'US';
  const formCountry = country || defaultCountry;

  const handleCloseAddressModal = () => setIsOpenAddressModal(false);
  const handleOpenAddressModal = (address: NormalizedAddress['userAddress'], modalName: string) => {
    if (address.isDefault && address.id !== defaultAddressId) {
      address.isDefault = false;
    }

    setCurrentAddressId(address.id);
    setCurrentModal(modalName);

    setIsOpenAddressModal(true);
  };

  const handleEditAddress = (address: NormalizedAddress['userAddress']) => {
    handleOpenAddressModal(address, ADDRESS_MODAL_NAMES.edit);
  };

  const handleOnClickAddAddressButton = () =>
    handleOpenAddressModal({} as NormalizedAddress['userAddress'], ADDRESS_MODAL_NAMES.add);

  const handleError = (error: string | { errors: string[] }) => {
    if (typeof error === 'string') {
      showErrorToast(error, undefined, { containerId: 'address-toast' });
    } else {
      showErrorToast(error.errors?.[0] || 'Unexpected error', undefined, {
        containerId: 'address-toast',
      });
    }
  };

  const editAddress = async (payloadToSubmit: unknown) => {
    try {
      const addressUpdated = (await updateAddress(
        payloadToSubmit,
        currentAddress.id,
      )) as NormalizedAddress['userAddress'];
      await clearUserCache();
      setAddresses((prevAddresses) =>
        prevAddresses.map((address) => {
          if (addressUpdated.isDefault && address.userAddress.id !== addressUpdated.id) {
            return {
              ...address,
              userAddress: {
                ...address.userAddress,
                isDefault: false,
              },
            };
          }
          return address.userAddress.id === addressUpdated.id
            ? { ...address, userAddress: addressUpdated }
            : address;
        }),
      );
      handleCloseAddressModal();
      closeVerificationModal();
      if (setSelectedAddressId) {
        setSelectedAddressId(addressUpdated.id);
      }
      if (addressUpdated.isDefault) {
        setDefaultAddressId(addressUpdated.id);
      }
    } catch (error: unknown) {
      handleError(error as string);
    }
  };

  const addAddress = async (payloadToSubmit: Omit<NormalizedAddress['userAddress'], 'id'>) => {
    try {
      const addressAddedId = (await addAddressService(payloadToSubmit)) as number;
      await clearUserCache();
      setAddresses((prevAddresses) => {
        if (payloadToSubmit.isDefault) {
          const updatedAddresses = prevAddresses.map((address) => ({
            ...address,
            userAddress: {
              ...address.userAddress,
              isDefault: address.userAddress.id === addressAddedId ? true : false,
            },
          }));

          return [
            ...updatedAddresses,
            {
              userAddress: {
                id: addressAddedId,
                ...payloadToSubmit,
                zipcode: payloadToSubmit.zipCode,
              },
            },
          ];
        } else {
          return [
            ...prevAddresses,
            {
              userAddress: {
                id: addressAddedId,
                ...payloadToSubmit,
                zipcode: payloadToSubmit.zipCode,
              },
            },
          ];
        }
      });
      handleCloseAddressModal();
      closeVerificationModal();
      if (onNewAddressCreated) {
        onNewAddressCreated(addressAddedId);
      }
      if (payloadToSubmit.isDefault) {
        setDefaultAddressId(addressAddedId);
      }
    } catch (error: unknown) {
      handleError(error as string);
    }
  };

  const removeAddress = async (addressId: number) => {
    try {
      await removeAddressService(addressId);
      await clearUserCache();
      setAddresses(addresses.filter((address) => address.userAddress.id !== addressId));
    } catch (error: unknown) {
      handleError(error as string);
    }
  };

  const verifyAddress = async (
    payloadToSubmit: Partial<NormalizedAddress['userAddress']>,
    methodHandler: (payload: NormalizedAddress['userAddress']) => void,
  ) => {
    const { city, state, addressLine1, addressLine2, country, zipCode, zipcode } = payloadToSubmit;

    const verifyAddressPayload = {
      city,
      state,
      countryCode: country,
      primaryLine: addressLine1,
      secondaryLine: addressLine2,
      zip: zipCode || zipcode,
    };

    try {
      const validationResponse = (await verifyAddressService(verifyAddressPayload)) as {
        success: boolean;
        deliveryStatus: string;
        verified: {
          fffUndeliverable: boolean;
        };
        submitted: {
          fffUndeliverable: boolean;
        };
      };
      setVerifyingAddress(true);
      if (validationResponse.success) {
        const { deliveryStatus, submitted } = validationResponse;
        const { fffUndeliverable } = submitted;
        const validation = handleAddressValidation(deliveryStatus, fffUndeliverable);
        setVerifyingAddress(false);
        switch (validation) {
          case 'success': {
            methodHandler(payloadToSubmit as NormalizedAddress['userAddress']);
            break;
          }
          case 'suggested':
            setVerificationModalOpen('suggested');
            setVerificationModalData(validationResponse);
            break;
          default:
            // undeliverable
            setVerificationModalOpen('undeliverable');
            setVerificationModalData(validationResponse);
        }
      }
    } catch (_) {
      setVerifyingAddress(false);
      showErrorToast(
        'Address Change Unsuccessful',
        "We're experiencing some technical difficulties. Please try again later.",
        {
          containerId: 'address-toast',
        },
      );
    }
  };

  const closeVerificationModal = () => {
    setVerificationModalOpen('');
    setVerificationModalData({
      submitted: {},
      verified: {},
    });
  };

  const handleClickUseThisAddress = async (
    suggestedSelected: boolean,
    suggestedData: Record<string, unknown>,
    resetSuggestedState: () => void,
  ) => {
    const data = suggestedSelected ? { ...payload, ...suggestedData } : payload;
    if (currentModal === ADDRESS_MODAL_NAMES.edit) {
      await editAddress(data as NormalizedAddress['userAddress']);
    } else if (currentModal === ADDRESS_MODAL_NAMES.add) {
      await addAddress(data as NormalizedAddress['userAddress']);
    }
    resetSuggestedState();
  };

  useEffect(() => {
    if (addressesData) {
      setAddresses(addressesData);
      const currentDefaultAddress = addressesData.find((address) => address.userAddress.isDefault);

      if (currentDefaultAddress) {
        const {
          userAddress: { id },
        } = currentDefaultAddress;
        if (!selectedAddressId && setSelectedAddressId) {
          setSelectedAddressId(id);
        }
        setDefaultAddressId(id);
      }
    }
  }, [addressesData]);

  const submitAddress = (formData: NormalizedAddress['userAddress']) => {
    const {
      address1,
      address2,
      city,
      firstName,
      lastName,
      phone,
      country,
      region,
      zipcode,
      isDefault,
    } = formData;

    const payloadToSubmit: Partial<NormalizedAddress['userAddress']> = {
      addressLine1: address1,
      addressLine2: address2,
      city,
      state: get(find(stateList, ['id', Number(region)]), 'stateCode'),
      zipCode: zipcode,
      country: get(COUNTRY_SELECTION, [country, 'code']),
      contactNumber: phone,
      firstName,
      lastName,
      isDefault: currentAddress.isDefault || isDefault,
    };

    setPayload(payloadToSubmit);
    if (currentModal === ADDRESS_MODAL_NAMES.edit) {
      verifyAddress(payloadToSubmit, editAddress);
    } else if (currentModal === ADDRESS_MODAL_NAMES.add) {
      verifyAddress(payloadToSubmit, addAddress);
    }
  };

  if (addresses.length === 0) {
    return null;
  }

  return (
    <>
      <Toast containerId="address-toast" />
      <div className={styles.container}>
        <AddressList
          addresses={addresses}
          selectedAddressId={selectedAddressId}
          setSelectedAddressId={setSelectedAddressId}
          handleEditAddress={handleEditAddress}
          handleRemoveAddress={removeAddress}
          layoutType={layoutType}
        />
        <AddAddressButton
          showMaxAddressLabel={addresses.length > 9}
          handleClick={handleOnClickAddAddressButton}
        />
      </div>
      <Modal isOpen={isOpenAddressModal} className={styles.modal}>
        <div>
          <div className={styles.header}>
            <h1>{ADDRESS_MODAL_TITLE[currentModal]}</h1>
            <Times onClick={handleCloseAddressModal} />
          </div>
          {currentAddress.isDefault && <h2>Primary Shipping Address</h2>}
          <AddressForm
            firstName={firstName}
            lastName={lastName}
            address1={addressLine1}
            address2={addressLine2}
            city={city}
            region={state}
            country={getCountryId(formCountry)}
            zipcode={zipcode || currentAddress.zipCode}
            phone={contactNumber}
            isDefault={isDefault}
            submitButtonName={
              layoutType === ADDRESS_LAYOUT_TYPES.userAccount ? 'SAVE' : 'USE THIS ADDRESS'
            }
            onSubmit={submitAddress}
            cancelButtonName="CANCEL"
            onCancel={handleCloseAddressModal}
            getStateList={setStateList}
            showPhoneNumber
          />
        </div>
      </Modal>
      <AddressVerificationModal
        modal={verificationModalOpen as 'suggested' | 'undeliverable'}
        data={
          verificationModalData as {
            submitted: NormalizedAddress['userAddress'];
            verified: NormalizedAddress['userAddress'];
          }
        }
        loading={verifyingAddress}
        closeModal={closeVerificationModal}
        payload={payload}
        onClickUseThisAddress={handleClickUseThisAddress}
        onClickUseAsEntered={currentModal === ADDRESS_MODAL_NAMES.edit ? editAddress : addAddress}
      />
    </>
  );
};

export default Address;
